// askSmart-ui/src/pages/TOSP/components/EstoppelTemplateEditor.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  loadEstoppelTemplateOutline,
  saveEstoppelMapping,
  saveEstoppelTemplateParagraph,
  TemplateOutline,
  TemplateParagraph,
} from "../tospApi";

interface Props {
  /** Changes when a new template is uploaded, so the document is re-read. */
  templateKey: string;
}

const textOf = (p: TemplateParagraph) => p.runs.map((r) => r.text).join("");

// The template, drawn on most of the page, and the Map beside it.
//
// The admin writes the template in Word with two kinds of markup:
//   [LABEL]      a field — the Map lists every one, and a TOSP field is chosen for it
//   { … }        optional wording — kept only when every field inside has a value
//
// Fields are marked yellow and optional wording shaded blue, exactly where the
// filler will act on them. A bracket that doesn't pair up is marked red where
// it sits, and the page jumps to it: click any paragraph to fix its text right
// here, and the change is written back into the Word file.
export default function EstoppelTemplateEditor({ templateKey }: Props) {
  const [outline, setOutline] = useState<TemplateOutline | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  /** The paragraph open for editing, and its text as typed. */
  const [editing, setEditing] = useState<{ index: number; text: string } | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const jumpedFor = useRef<string | null>(null);

  const load = useCallback(async () => {
    const o = await loadEstoppelTemplateOutline();
    setOutline(o);
    // Keep any mapping changes not yet saved; take the rest from the server,
    // which may have suggested fields for labels an edit just introduced.
    setMapping((prev) => ({ ...o.mapping, ...prev }));
    return o;
  }, []);

  useEffect(() => {
    setOutline(null);
    setError(null);
    setMapping({});
    setEditing(null);
    load().catch((err) => setError(err instanceof Error ? err.message : "Couldn't read the template"));
  }, [templateKey, load]);

  const problems = useMemo(
    () => (outline?.paragraphs || []).filter((p) => p.errors.length > 0),
    [outline],
  );

  const scrollTo = (selector: string) =>
    pageRef.current?.querySelector(selector)?.scrollIntoView({ behavior: "smooth", block: "center" });

  const openEditor = (p: TemplateParagraph) => {
    setEditing({ index: p.index, text: textOf(p) });
    setEditError(null);
  };

  /** The next paragraph with a problem after the one being edited (or the first). */
  const nextProblem = () => {
    if (!problems.length) return;
    const after = problems.find((p) => p.index > (editing?.index ?? -1)) || problems[0];
    openEditor(after);
    setTimeout(() => scrollTo(`[data-para="${after.index}"]`), 0);
  };

  // A template arriving with problems opens straight onto the first one.
  useEffect(() => {
    if (!outline || jumpedFor.current === templateKey) return;
    jumpedFor.current = templateKey;
    if (problems.length) nextProblem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outline]);

  const saveParagraph = async () => {
    if (!editing || !outline) return;
    const p = outline.paragraphs.find((x) => x.index === editing.index);
    if (!p) return;
    const before = textOf(p);
    if (before === editing.text) {
      setEditing(null);
      return;
    }
    setEditBusy(true);
    setEditError(null);
    try {
      await saveEstoppelTemplateParagraph(editing.index, before, editing.text);
      const fresh = await load();
      const now = fresh.paragraphs.find((x) => x.index === editing.index);
      // Still wrong: stay on it. Fixed: move on to the next problem, if any.
      if (now && now.errors.length) {
        setEditing({ index: now.index, text: textOf(now) });
      } else {
        const next = fresh.paragraphs.find((x) => x.errors.length && x.index > editing.index)
          || fresh.paragraphs.find((x) => x.errors.length);
        if (next) {
          setEditing({ index: next.index, text: textOf(next) });
          setTimeout(() => scrollTo(`[data-para="${next.index}"]`), 0);
        } else {
          setEditing(null);
        }
      }
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Couldn't save");
    } finally {
      setEditBusy(false);
    }
  };

  const groups = useMemo(() => {
    const by = new Map<string, { key: string; label: string }[]>();
    for (const f of outline?.fields || []) {
      if (!by.has(f.group)) by.set(f.group, []);
      by.get(f.group)!.push(f);
    }
    return [...by.entries()];
  }, [outline]);

  const labels = outline?.labels || [];
  const saved = outline?.mapping || {};
  const relevant = (m: Record<string, string>) =>
    Object.fromEntries(labels.filter((l) => m[l.label]).map((l) => [l.label, m[l.label]]));
  const dirty = JSON.stringify(relevant(mapping)) !== JSON.stringify(relevant(saved));
  const unmapped = labels.filter((l) => !mapping[l.label]);

  const saveMapping = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const result = await saveEstoppelMapping(relevant(mapping));
      setOutline((o) => (o ? { ...o, mapping: result.mapping } : o));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const goTo = (label: string) => {
    setFocused(label);
    scrollTo(`[data-label="${CSS.escape(label)}"]`);
  };

  // Each run is cut wherever a field, an optional piece or a problem starts or
  // ends, so every piece of text knows what it belongs to.
  const renderRuns = (p: TemplateParagraph) => {
    let cursor = 0;
    return p.runs.map((run, i) => {
      const start = cursor;
      cursor += run.text.length;
      const cuts = new Set<number>([0, run.text.length]);
      for (const r of [...p.fields, ...p.blocks, ...p.errors]) {
        for (const edge of [r.start, r.end]) {
          if (edge > start && edge < cursor) cuts.add(edge - start);
        }
      }
      const edges = [...cuts].sort((a, b) => a - b);
      const style: React.CSSProperties = {
        fontWeight: run.bold ? 700 : undefined,
        fontStyle: run.italic ? "italic" : undefined,
        textDecoration: run.underline ? "underline" : undefined,
        color: run.color ? `#${run.color}` : undefined,
      };
      return (
        <React.Fragment key={i}>
          {run.tab && <span className="tosp-template-tab" aria-hidden="true" />}
          {edges.slice(0, -1).map((a, k) => {
            const at = start + a;
            const text = run.text.slice(a, edges[k + 1]);
            const field = p.fields.find((f) => at >= f.start && at < f.end);
            const optional = p.blocks.some((b) => at >= b.start && at < b.end);
            const problem = p.errors.find((e) => at >= e.start && at < e.end);
            const classes = [
              optional ? "tosp-template-optional" : "",
              field ? (mapping[field.label] ? "tosp-template-field" : "tosp-template-field tosp-template-field-unmapped") : "",
              field && field.label === focused ? "tosp-template-field-focus" : "",
              problem ? "tosp-template-problem" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <span
                key={k}
                style={style}
                className={classes || undefined}
                data-label={field?.label}
                title={problem?.message}
              >
                {text}
              </span>
            );
          })}
        </React.Fragment>
      );
    });
  };

  if (error) return <div className="tosp-banner tosp-banner-error"><span>{error}</span></div>;
  if (!outline) {
    return (
      <div className="tosp-grid-empty">
        <span className="tosp-spinner" aria-hidden="true" /> Reading the template…
      </div>
    );
  }

  return (
    <div className="tosp-template-editor">
      <div className="tosp-template-page-wrap">
        {problems.length > 0 && (
          <div className="tosp-template-problembar">
            <span>
              ⚠ {problems.length} paragraph{problems.length === 1 ? " has" : "s have"} brackets or braces
              that don't pair up. Click a paragraph to fix it here.
            </span>
            <button type="button" className="tosp-btn-secondary" onClick={nextProblem}>
              Next ▸
            </button>
          </div>
        )}

        <div className="tosp-template-page" ref={pageRef}>
          {outline.paragraphs.map((p) =>
            editing?.index === p.index ? (
              <div key={p.index} data-para={p.index} className="tosp-template-edit">
                <textarea
                  className="manage-input"
                  autoFocus
                  rows={Math.max(3, Math.ceil(editing.text.length / 90))}
                  value={editing.text}
                  disabled={editBusy}
                  onChange={(e) => setEditing({ index: p.index, text: e.target.value.replace(/[\r\n]+/g, " ") })}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setEditing(null);
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      saveParagraph();
                    }
                  }}
                />
                {p.errors.map((err, i) => (
                  <p key={i} className="tosp-field-error">
                    {err.message}: “…{textOf(p).slice(Math.max(0, err.start - 20), err.end + 20)}…”
                  </p>
                ))}
                {editError && <p className="tosp-field-error">{editError}</p>}
                <div className="tosp-template-edit-actions">
                  <button type="button" className="btn-cancel" disabled={editBusy} onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                  <button type="button" className="btn-save" disabled={editBusy} onClick={saveParagraph}>
                    {editBusy ? (
                      <>
                        <span className="tosp-spinner" aria-hidden="true" /> Saving…
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <p
                key={p.index}
                data-para={p.index}
                className={`tosp-template-para${p.errors.length ? " tosp-template-para-problem" : ""}`}
                style={{
                  textAlign: p.align === "both" ? "justify" : p.align,
                  paddingLeft: p.level ? `${p.level * 2.25}em` : undefined,
                }}
                title="Click to edit this paragraph"
                onClick={() => {
                  // A click that ends a text selection isn't a request to edit.
                  if (window.getSelection()?.toString()) return;
                  openEditor(p);
                }}
              >
                {p.number && (
                  <span className="tosp-template-number" aria-hidden="true">
                    {p.number}
                  </span>
                )}
                {textOf(p) || p.runs.some((r) => r.tab) ? renderRuns(p) : " "}
              </p>
            ),
          )}
        </div>
      </div>

      <aside className="tosp-template-map">
        <div className="tosp-template-map-head">
          <h3>Map</h3>
          <button
            type="button"
            className={`tosp-btn-save-changes${dirty ? " tosp-btn-save-changes-on" : ""}`}
            disabled={!dirty || saving}
            onClick={saveMapping}
          >
            {saving ? (
              <>
                <span className="tosp-spinner" aria-hidden="true" /> Saving…
              </>
            ) : (
              "Save Mapping"
            )}
          </button>
        </div>
        {saveError && <p className="tosp-field-error">{saveError}</p>}

        <h4>
          Fields{" "}
          <span className="tosp-field-hint">
            {labels.length === 0 ? "none yet" : unmapped.length ? `${unmapped.length} still to map` : "all mapped"}
          </span>
        </h4>
        <ul className="tosp-template-map-list">
          {labels.map(({ label, count }) => (
            <li
              key={label}
              className={`${label === focused ? "tosp-template-map-focus" : ""}${
                mapping[label] ? "" : " tosp-template-map-unmapped"
              }`}
              onClick={() => goTo(label)}
            >
              <div className="tosp-template-map-label">
                [{label}]
                {count > 1 && <span className="tosp-field-hint"> ×{count}</span>}
              </div>
              <select
                className="manage-input"
                value={mapping[label] || ""}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const key = e.target.value;
                  setMapping((prev) => {
                    const next = { ...prev };
                    if (key) next[label] = key;
                    else delete next[label];
                    return next;
                  });
                }}
              >
                <option value="">— Choose a TOSP field —</option>
                {groups.map(([group, fields]) => (
                  <optgroup key={group} label={group}>
                    {fields.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </li>
          ))}
        </ul>

        <h4>Optional wording</h4>
        {outline.blocks.length === 0 ? (
          <div className="tosp-template-map-empty">{"None — nothing in {curly braces}."}</div>
        ) : (
          <ul className="tosp-template-map-list">
            {outline.blocks.map((b, i) => (
              <li key={i} onClick={() => b.labels[0] && goTo(b.labels[0])}>
                <div>
                  <span className="tosp-template-optional">{b.text}</span>
                  <div className="tosp-field-hint">
                    Shown only when {b.labels.map((l) => `[${l}]`).join(" and ")}{" "}
                    {b.labels.length > 1 ? "all have values" : "has a value"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
}
